/* Standalone PDP choices. The matching-cushion module owns its own UI and data. */
(() => {
  if (customElements.get('teak-product-choice')) return;
  const escape = value => String(value ?? '').replace(/[&<>"']/g, ch => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[ch]));
  let nextId = 0;
  class ProductChoice extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      const source = this.querySelector('[data-choice-config]');
      if (!source) return;
      this.config = JSON.parse(source.textContent);
      this.variants = this.config.variants;
      this.root = this.closest('[data-teak-pshow]');
      if (!this.root || !this.variants.length) return;
      this.ready = true;
      this.current = this.variants.find(v => String(v.id) === String(this.config.selected)) || this.variants[0];
      this.colourIndex = this.config.options.findIndex(n => /colou?r/i.test(n));
      this.isRound = /round/i.test(this.config.title) && this.colourIndex < 0;
      this.isBench = /bench/i.test(this.config.title) && !/cushion/i.test(this.config.title);
      this.grouped = this.config.options.length === 1 && !this.isRound && this.variants.every(v => this.dimensions(v.options[0]));
      this.heading = this.config.options.length > 1 ? 'Choose your options' : this.colourIndex === 0 ? 'Choose your colour' : this.isRound ? 'Choose your diameter' : this.isBench ? 'Choose your length' : 'Choose your size';
      if (this.config.options.length > 1 && /cushion/i.test(this.config.title)) this.heading = 'Choose your cushion';
      const id = 'ttp-choice-' + ++nextId;
      this.innerHTML = `<button type="button" class="ttp-choice-trigger" aria-haspopup="dialog" aria-expanded="false" aria-controls="${id}"></button>
        <dialog id="${id}" class="ttp-choice-dialog" aria-labelledby="${id}-title">
          <header class="ttp-choice-head"><div><p class="ttp-choice-product">${escape(this.config.title)}</p><h2 id="${id}-title">${this.heading}</h2></div><button type="button" class="ttp-choice-close" aria-label="Close options"><svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true"><path d="m4 4 10 10M14 4 4 14" stroke="currentColor" stroke-width="1.5"/></svg></button></header>
          <div class="ttp-choice-content"></div><footer class="ttp-choice-foot"><div class="ttp-choice-summary" aria-live="polite"></div><button type="button" class="ttp-choice-confirm">Use this ${this.colourIndex === 0 ? 'colour' : /cushion/i.test(this.config.title) ? 'cushion' : 'size'}</button></footer>
        </dialog>`;
      this.trigger = this.querySelector('.ttp-choice-trigger');
      this.dialog = this.querySelector('dialog');
      this.content = this.querySelector('.ttp-choice-content');
      this.confirmLabel = this.querySelector('.ttp-choice-confirm').textContent;
      this.trigger.addEventListener('click', () => this.open());
      this.querySelector('.ttp-choice-close').addEventListener('click', () => this.dialog.close());
      this.dialog.addEventListener('close', () => {
        this.trigger.setAttribute('aria-expanded', 'false');
        this.trigger.focus({preventScroll:true});
      });
      this.dialog.addEventListener('click', e => {
        const rect = this.dialog.getBoundingClientRect();
        if (e.target === this.dialog && (e.clientX < rect.left || e.clientX > rect.right || e.clientY < rect.top || e.clientY > rect.bottom)) this.dialog.close();
      });
      this.content.addEventListener('click', e => {
        const choice = e.target.closest('[data-choice-id]');
        if (choice && !choice.disabled) {
          this.draft = this.variants.find(v => String(v.id) === choice.dataset.choiceId);
          this.renderChoices();
          this.content.querySelector(`[data-choice-id="${this.draft.id}"]`)?.focus({preventScroll:true});
        }
        const axis = e.target.closest('[data-choice-axis]');
        if (axis && !axis.disabled) {
          const index = Number(axis.dataset.choiceAxis), value = axis.dataset.value;
          // Waitlist (26-10-2026): sold-out combinations stay choosable; prefer an in-stock match.
          const matches = this.variants.filter(v => v.options[index] === value && v.options.slice(0,index).every((o,i) => o === this.draft.options[i]));
          const keep = matches.filter(v => v.options.slice(index+1).every((o,i) => o === this.draft.options[index+1+i]));
          this.draft = keep.find(v => v.available) || keep[0] || matches.find(v => v.available) || matches[0];
          this.renderChoices();
          Array.from(this.content.querySelectorAll('[data-choice-axis]')).find(b => b.dataset.choiceAxis === String(index) && b.dataset.value === value)?.focus({preventScroll:true});
        }
      });
      this.querySelector('.ttp-choice-confirm').addEventListener('click', () => {
        if (!this.draft) return;
        const id = this.draft.id;
        this.dialog.close();
        this.dispatchEvent(new CustomEvent('teak:variant-choose', {bubbles:true, detail:{id}}));
      });
      this.onApplied = e => {
        const variant = this.variants.find(v => String(v.id) === String(e.detail.variant.id));
        if (variant) { this.current = variant; this.renderTrigger(); }
      };
      this.root.addEventListener('teak:variant-applied', this.onApplied);
      this.renderTrigger();
      // Retain native controls as the fallback and the existing variant source.
      this.nextElementSibling?.classList.add('ttp-choice-native');
      this.hidden = false;
    }
    disconnectedCallback() {
      if (this.dialog?.open) this.dialog.close();
      if (this.root && this.onApplied) this.root.removeEventListener('teak:variant-applied',this.onApplied);
    }
    money(cents) {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-AU', {style:'currency', currency:this.config.currency || 'AUD', currencyDisplay:'narrowSymbol', maximumFractionDigits:cents % 100 ? 2 : 0}).format(cents/100);
    }
    dimensions(value) { return value.match(/^L?(\d+(?:\.\d+)?)\s*(?:cm)?\s*[x×]\s*W?(\d+(?:\.\d+)?)/i); }
    clean(value) {
      const bare = value.split('(')[0].trim();
      if (this.isRound) { const match = bare.match(/\d+(?:\.\d+)?/); if (match) return 'Ø ' + match[0] + ' cm'; }
      const dims = this.dimensions(bare);
      if (dims) return dims[1] + ' × ' + dims[2] + ' cm';
      return bare.replace(/(\d)cm\b/g,'$1 cm');
    }
    label(v) { return v.options.map(o => this.clean(o)).join(' · '); }
    sale(v) { return v.compare > v.price; }
    price(v, showBefore = false) {
      return `<span class="ttp-choice-price${this.sale(v)?' is-sale':''}">${showBefore && this.sale(v)?`<s class="ttp-choice-before"><span class="visually-hidden">Was </span>${this.money(v.compare)}</s>`:''}<span class="ttp-choice-now">${this.money(v.price)}</span>${this.sale(v)?`<span class="ttp-choice-discount">${this.config.saleLabel ? escape(this.config.saleLabel) : '−' + Math.round((v.compare-v.price)*100/v.compare) + '%'}</span>`:''}</span>`;
    }
    swatch(v) {
      return v.swatch ? `<img class="ttp-choice-swatch" src="${escape(v.swatch)}" alt="" width="32" height="32" loading="lazy">` : '';
    }
    renderTrigger() {
      this.trigger.innerHTML = `<span class="ttp-choice-trigger-main"><span class="ttp-choice-label">${this.colourIndex === 0 ? 'Colour' : this.config.options.length > 1 ? 'Options' : 'Size'}</span><span class="ttp-choice-trigger-value">${this.swatch(this.current)}${escape(this.label(this.current))}</span></span>${this.price(this.current)}<svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="m3 5 4 4 4-4" stroke="currentColor" stroke-width="1.5"/></svg>`;
    }
    open() {
      this.draft = this.current;
      this.renderChoices();
      this.content.scrollTop = 0;
      this.dialog.showModal();
      this.trigger.setAttribute('aria-expanded','true');
      this.querySelector('.ttp-choice-close').focus({preventScroll:true});
      this.content.scrollTop = 0;
    }
    row(v, label, note) {
      return `<button type="button" class="ttp-choice-row" data-choice-id="${v.id}" aria-pressed="${v.id === this.draft.id}"><span class="ttp-choice-radio" aria-hidden="true">${v.id === this.draft.id?'✓':''}</span><span class="ttp-choice-row-main"><span class="ttp-choice-row-name">${this.colourIndex >= 0 ? this.swatch(v):''}${escape(label)}</span>${note?`<small>${escape(note)}</small>`:''}${v.available?'':(v.restock?`<small class="ttp-choice-expected">Expected ${escape(v.restock)}</small>`:'<small>Sold out · join the waitlist</small>')}</span>${this.price(v, true)}</button>`;
    }
    renderChoices() {
      let html = '';
      if (this.grouped) {
        const groups = new Map();
        this.variants.forEach(v => { const d = this.dimensions(v.options[0]); if (!groups.has(d[1])) groups.set(d[1],[]); groups.get(d[1]).push(v); });
        const entries = Array.from(groups).sort((a,b)=>Number(a[0])-Number(b[0]));
        // Balance complete length groups across columns; never split a group.
        let split = 1, best = Infinity;
        for (let i=1;i<entries.length;i++) {
          const height = part => part.reduce((n,g)=>n+g[1].length*64+40,0);
          const difference = Math.abs(height(entries.slice(0,i))-height(entries.slice(i)));
          if (difference < best) {best=difference;split=i;}
        }
        const columns = entries.length > 1 ? [entries.slice(0,split),entries.slice(split)] : [entries];
        html = `<div class="ttp-choice-groups${columns.length === 1?' is-single':''}">` + columns.map(column => '<div>'+column.map(([length, variants]) => `<section class="ttp-choice-length"><h3>${escape(length)} cm long</h3>${variants.map(v => this.row(v,this.dimensions(v.options[0])[2]+' cm wide',v.options[0].match(/\(([^)]+)\)/)?.[1])).join('')}</section>`).join('')+'</div>').join('')+'</div>';
      } else if (this.config.options.length > 1) {
        const last = this.config.options.length-1;
        this.config.options.slice(0,last).forEach((name,index) => {
          const values = [...new Set(this.variants.map(v=>v.options[index]))];
          html += `<fieldset class="ttp-choice-axis"><legend>${index+1}. ${escape(name)}</legend><div>`+values.map(value => {
            const enabled = this.variants.some(v=>v.options[index]===value && v.options.slice(0,index).every((o,i)=>o===this.draft.options[i]));
            return `<button type="button" data-choice-axis="${index}" data-value="${escape(value)}" aria-pressed="${this.draft.options[index]===value}" ${enabled?'':'disabled'}>${escape(this.clean(value))}</button>`;
          }).join('')+'</div></fieldset>';
        });
        html += `<h3 class="ttp-choice-axis-label">${last+1}. ${escape(this.config.options[last])}</h3>`;
        html += this.variants.filter(v=>v.options.slice(0,last).every((o,i)=>o===this.draft.options[i])).map(v=>this.row(v,this.clean(v.options[last]),'')).join('');
      } else {
        html = this.variants.map(v=>this.row(v,this.clean(v.options[0]),v.options[0].match(/\(([^)]+)\)/)?.[1])).join('');
      }
      this.content.innerHTML = html;
      this.querySelector('.ttp-choice-summary').innerHTML = `<span>${escape(this.label(this.draft))}</span>${this.price(this.draft)}${this.sale(this.draft)?`<small>Was <s>${this.money(this.draft.compare)}</s> · Save ${this.money(this.draft.compare-this.draft.price)}</small>`:''}`;
      this.querySelector('.ttp-choice-confirm').disabled = false;
      this.querySelector('.ttp-choice-confirm').innerHTML = `<span class="ttp-choice-confirm-desktop">${escape(this.confirmLabel)}</span><span class="ttp-choice-confirm-mobile">Use ${escape(this.label(this.draft))}</span>`;
    }
  }
  customElements.define('teak-product-choice',ProductChoice);
})();
